import * as openpgp from 'openpgp';
import { generate } from 'generate-password-browser';

export { sha512 } from 'js-sha512';

type UserID = {
  name: string;
  email: string;
}

export const shrink = (message: string) => {
  return message.replace('-----BEGIN PGP MESSAGE-----\n\n', '<')
    .replace('\n-----END PGP MESSAGE-----\n', '>')
    .replace('-----BEGIN PGP PRIVATE KEY BLOCK-----\n\n', '[')
    .replace('\n-----END PGP PRIVATE KEY BLOCK-----\n', ']')
    .replace('-----BEGIN PGP PUBLIC KEY BLOCK-----\n\n', '{')
    .replace('\n-----END PGP PUBLIC KEY BLOCK-----\n', '}')
    .replace(/\n/g, '#');
}

export const expand = (message: string) => {
  return message.replace(/#/g, '\n')
    .replace('<', '-----BEGIN PGP MESSAGE-----\n\n')
    .replace('>', '\n-----END PGP MESSAGE-----\n')
    .replace( '[', '-----BEGIN PGP PRIVATE KEY BLOCK-----\n\n')
    .replace(']', '\n-----END PGP PRIVATE KEY BLOCK-----\n')
    .replace('{', '-----BEGIN PGP PUBLIC KEY BLOCK-----\n\n')
    .replace('}', '\n-----END PGP PUBLIC KEY BLOCK-----\n') 
}

export const generateKeys = async (userID: UserID, passphrase?: string) => {
  if (!passphrase?.length) {
    throw new Error('Needs a valid passphrase')
  }

  const { privateKey: privateKeyArmored, publicKey: publicKeyArmored } = await openpgp.generateKey({
    type: 'ecc', // Type of the key, defaults to ECC
    curve: 'curve25519', // ECC curve name, defaults to curve25519
    userIDs: [userID],
    passphrase
  });

  return {
    privateKeyArmored: shrink(privateKeyArmored),
    publicKeyArmored: shrink(publicKeyArmored)
  }
}

export const generateSessionKey = async (encryptionKeys: any, passwords?: string[] | string) => {
  const sessionKey = generate({length: 64, numbers: true, excludeSimilarCharacters: false, symbols: true});

  const encSessionKey = await encryptSessionKey(sessionKey, encryptionKeys, passwords)

  return [ sessionKey, encSessionKey ];
}

export const encryptSessionKey = async (sessionKey: string, encryptionKeys: any, passwords?: string[] | string) => {
  if (!encryptionKeys) {
    throw new Error('Needs a valid public key')
  }

  const encText = await openpgp.encrypt({
    message: await openpgp.createMessage({ text: sessionKey }), // input as Message object
    encryptionKeys,
    passwords
  }) as string;

  return shrink(encText);
}

export const decryptSessionKeyWithPassword = async (armoredMessage: string, passwords?: string[] | string) => {
  return await decryptSessionKey(armoredMessage, null, passwords);
}

export const decryptSessionKey = async (armoredMessage: string, decryptionKeys?: any, passwords?: string[] | string) => {
  const message = await openpgp.readMessage({
    armoredMessage: expand(armoredMessage)
  });

  const params: any = {
    message
  }

  if (decryptionKeys) {
    params.decryptionKeys = decryptionKeys;
  }

  if (passwords) {
    params.passwords = passwords;
  }

  const { data } = await openpgp.decrypt(params);

  return data as string;
}

export const encryptBinary = async (binary: any, passwords: any) => {
  if (!passwords?.length) {
    throw new Error('Needs a valid password')
  }

  const message = await openpgp.createMessage({ binary });

  return await openpgp.encrypt({
    message,
    passwords,
    format: 'binary'
  });
}

export const decryptBinary = async (binaryMessage: any, passwords: any) => {
  const encryptedMessage = await openpgp.readMessage({
    binaryMessage
  });

  const { data: decrypted } = await openpgp.decrypt({
    message: encryptedMessage,
    passwords,
    format: 'binary'
  });

  return decrypted;
}

export const encrypt = async (data: any, passwords: any) => {
  if (!passwords?.length) {
    throw new Error('Needs a valid password')
  }

  const message = await openpgp.createMessage({ text: JSON.stringify(data) });

  const encData = await openpgp.encrypt({
    message,
    passwords,
    format: 'armored'
  }) as string;

  return shrink(encData);
}

export const decrypt = async (armoredMessage: string, passwords: any) => {
  const encryptedMessage = await openpgp.readMessage({
    armoredMessage: expand(armoredMessage)
  });

  const { data: decrypted } = await openpgp.decrypt({
    message: encryptedMessage,
    passwords,
    format: 'utf8'
  });

  return JSON.parse(decrypted as string);
}

export const decryptWithKey = async (armoredMessage: string, decryptionKeys: any) => {
  const message = await openpgp.readMessage({
    armoredMessage: expand(armoredMessage)
  });

  const { data: decrypted } = await openpgp.decrypt({
    message,
    decryptionKeys
  });

  return decrypted as string;
}

export const encryptWithKey = async (text: string, encryptionKeys: any) => {
  if (!encryptionKeys) {
    throw new Error('Needs a valid private key');
  }

  const encText = await openpgp.encrypt({
    message: await openpgp.createMessage({ text }), // input as Message object
    encryptionKeys
  }) as string;

  return shrink(encText);
}

export const changePrivateKeyPassword = async (privateKey: any, user: any) => {
  const { privateKey: changedPrivateKey } = await openpgp.reformatKey({
    privateKey,
    passphrase: user.password,
    userIDs: { name: `${user.first_name} ${user.last_name}`, email: user.email }
  });

  return shrink(changedPrivateKey);
}

export const decryptPackKey = async (pack: any, keys: any) => {
  const hash = window.location.hash?.slice(1);
  const password = hash?.length ? hash : null;

  if (password) {
    return await decryptSessionKeyWithPassword(pack.key, password);
  }
  
  if (keys?.private && keys.id === pack.key_id) {
    return await decryptSessionKey(pack.join_key || pack.key, keys.private);
  }
  
  return null;
}

export const reformatKey = async (user: any, oldPassphrase: string, newPassphrase: string) => {
  const privateKey = await openpgp.decryptKey({
    privateKey: await openpgp.readPrivateKey({ armoredKey: expand(user.private_key_armored) }),
    passphrase: oldPassphrase
  });
  
  const { privateKey: reformattedKey } = await openpgp.reformatKey({
    privateKey,
    passphrase: newPassphrase,
    userIDs: { name: `${user.first_name} ${user.last_name}`, email: user.email }
  });

  return shrink(reformattedKey);
}
